import React, { createContext, useContext, ReactNode, useMemo } from 'react';
import { useLiveShowQuery } from '../hooks/queries/useLiveShowQuery';
import { Show } from '../types/Show';

interface LiveShowContextType {
  currentLiveShow: Show | null;
  loading: boolean;
  error: string | null;
  isShowCurrentlyLive: (show: Show) => boolean;
  refetchLiveShow: () => void;
}

const LiveShowContext = createContext<LiveShowContextType | undefined>(undefined);

interface LiveShowProviderProps {
  children: ReactNode;
}

export const LiveShowProvider: React.FC<LiveShowProviderProps> = ({ children }) => {
  const { data: currentLiveShow, isLoading: loading, error, refetch } = useLiveShowQuery();

  // Function to check if a given show is currently live
  const isShowCurrentlyLive = useMemo(() => {
    return (show: Show): boolean => {
      if (!currentLiveShow || !show) return false;
      
      // Compare by show name and start time to determine if this is the current live show
      return (
        currentLiveShow.show === show.show &&
        currentLiveShow.date_start_time === show.date_start_time
      );
    };
  }, [currentLiveShow]);

  const contextValue: LiveShowContextType = {
    currentLiveShow: currentLiveShow || null,
    loading,
    error: error?.message || null,
    isShowCurrentlyLive,
    refetchLiveShow: refetch,
  };

  return (
    <LiveShowContext.Provider value={contextValue}>
      {children}
    </LiveShowContext.Provider>
  );
};

export const useLiveShowContext = (): LiveShowContextType => {
  const context = useContext(LiveShowContext);
  if (context === undefined) {
    throw new Error('useLiveShowContext must be used within a LiveShowProvider');
  }
  return context;
};
